import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterIcon, GridIcon, ListIcon, ChevronDownIcon } from 'lucide-react';
import { products } from '../data/products';
import { ProductGrid } from '../components/products/ProductGrid';
import { ProductFilters } from '../components/products/ProductFilters';
import { Select } from '../components/ui/Select';
import { Button } from '../components/ui/Button';
const sortOptions = [{
  value: 'featured',
  label: 'Featured'
}, {
  value: 'newest',
  label: 'Newest First'
}, {
  value: 'price-low',
  label: 'Price: Low to High'
}, {
  value: 'price-high',
  label: 'Price: High to Low'
}, {
  value: 'rating',
  label: 'Highest Rated'
}, {
  value: 'popular',
  label: 'Most Popular'
}];
export function ProductListingPage() {
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 200000]);
  const [sortBy, setSortBy] = useState('featured');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const filteredProducts = useMemo(() => {
    let result = [...products];
    // Category filter
    if (selectedCategory) {
      result = result.filter(p => p.categorySlug === selectedCategory);
    }
    // Brand filter
    if (selectedBrand) {
      result = result.filter(p => p.brand === selectedBrand);
    }
    // Price filter
    result = result.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);
    // Sorting
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'popular':
        result.sort((a, b) => b.reviewCount - a.reviewCount);
        break;
    }
    return result;
  }, [selectedCategory, selectedBrand, priceRange, sortBy]);
  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedBrand('');
    setPriceRange([0, 200000]);
  };
  const pageTitle = selectedCategory ? products.find(p => p.categorySlug === selectedCategory)?.category || 'Products' : 'All Products';
  return <div className="min-h-screen bg-gray-50 py-6" data-id="element-1546">
      <div className="max-w-7xl mx-auto px-4" data-id="element-1547">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-4" aria-label="Breadcrumb" data-id="element-1548">
          <ol className="flex items-center gap-2" data-id="element-1549">
            <li data-id="element-1550">
              <a href="/" className="hover:text-primary-600" data-id="element-1551">
                Home
              </a>
            </li>
            <li data-id="element-1552">/</li>
            <li className="text-gray-900 font-medium" data-id="element-1553">{pageTitle}</li>
          </ol>
        </nav>

        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-1554">
          <div data-id="element-1555">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-1556">
              {pageTitle}
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-1557">
              {filteredProducts.length} products found
            </p>
          </div>

          <div className="flex items-center gap-3" data-id="element-1558">
            {/* Mobile filter button */}
            <Button variant="outline" leftIcon={<FilterIcon className="w-4 h-4" data-id="element-1560" />} onClick={() => setShowMobileFilters(true)} className="lg:hidden" data-id="element-1559">
              Filters
            </Button>

            {/* Sort dropdown */}
            <div className="w-48" data-id="element-1561">
              <Select options={sortOptions} value={sortBy} onChange={setSortBy} data-id="element-1562" />
            </div>
          </div>
        </div>

        <div className="flex gap-6" data-id="element-1563">
          {/* Desktop filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0" data-id="element-1564">
            <div className="sticky top-32" data-id="element-1565">
              <ProductFilters selectedCategory={selectedCategory} selectedBrand={selectedBrand} priceRange={priceRange} onCategoryChange={setSelectedCategory} onBrandChange={setSelectedBrand} onPriceRangeChange={setPriceRange} onClearFilters={clearFilters} data-id="element-1566" />
            </div>
          </aside>

          {/* Product grid */}
          <main className="flex-1" data-id="element-1567">
            <ProductGrid products={filteredProducts} columns={3} data-id="element-1568" />

            {filteredProducts.length === 0 && <div className="text-center py-12" data-id="element-1569">
                <p className="text-gray-500 mb-4" data-id="element-1570">
                  No products match your filters
                </p>
                <Button variant="outline" onClick={clearFilters} data-id="element-1571">
                  Clear Filters
                </Button>
              </div>}
          </main>
        </div>

        {/* Mobile filters modal */}
        {showMobileFilters && <ProductFilters selectedCategory={selectedCategory} selectedBrand={selectedBrand} priceRange={priceRange} onCategoryChange={setSelectedCategory} onBrandChange={setSelectedBrand} onPriceRangeChange={setPriceRange} onClearFilters={clearFilters} isMobile onClose={() => setShowMobileFilters(false)} data-id="element-1572" />}
      </div>
    </div>;
}